/**
 * Breaker design tokens — Phase 0 redesign, chosen 2026-08-16.
 *
 * Single source of truth for the values every Breaker-identity surface
 * (marketing, dispatcher chrome + signup wizard, transactional email, the
 * driver app) draws from. Fleet-branded surfaces (portal, work-driver, a
 * fleet's own booking pages) are OUT OF SCOPE — they derive their palette
 * per-fleet via deriveBrandVars and never read these tokens.
 *
 * Picked from the /theme-preview comparison (apps/dispatcher):
 *   - identity blue: #0066FF
 *   - CTA shape: pill
 *   - wordmark: "BREAKER" in ink, full stop in the identity blue
 *
 * Colour law (the one rule every surface follows):
 *   - `identity` is brand + primary action. It is never used for status.
 *   - `green` / `amber` / `red` are STATUS ONLY, never identity/action.
 *     `red` is further reserved for danger/destructive specifically — it
 *     does not mean "primary CTA" anywhere, including screens (the driver
 *     app's old "red fills Claim it / Post" law) that used to read it that
 *     way. Those actions move to `identity`.
 *   - The coral/amber `celebration` wash marks completion beats only —
 *     never a resting state, never identity.
 */
export const light = {
    ground: '#FAFAF7',
    surface: '#FFFFFF',
    ink: '#101418',
    ink2: '#3A3D42',
    mute: '#6B6F76',
    line: '#E4E4DE',
    line2: '#D6D6CF',
    identity: '#0066FF',
    identityHover: '#0052CC',
    green: '#12B76A',
    amber: '#EF7D1A',
    /* Refreshed 2026-08-19 (dispatcher parity unit): #C72027 was the S78-era
       brand red carried over unrefreshed when red was demoted to status-only;
       the hex itself is retired everywhere. AA: 4.83 on surface, 4.62 on
       ground, 4.83 white-on-red. */
    red: '#D92D20',
};
/**
 * Dark-mode-first surfaces (the driver app defaults to dark — "the brand
 * scheme is the first impression"). green/amber/red carry over verbatim
 * from the pre-redesign driver spec (breaker-design-spec-v1.md §1) — those
 * values were already tuned for dark-ground contrast and the redesign
 * doesn't revisit them, only their scope (status-only, never identity).
 * identity is brightened relative to the light value for the same reason
 * the old aqua accent shifted lighter in dark mode: #0066FF reads slightly
 * muddy at small sizes against near-black.
 */
export const dark = {
    ground: '#14161A',
    surface: '#1B1E23',
    ink: '#F3F3F0',
    ink2: '#C9CBCE',
    mute: '#8B8F96',
    line: '#2C2F35',
    line2: '#383B42',
    identity: '#4C8BFF',
    identityHover: '#6FA1FF',
    green: '#3DD68C',
    amber: '#F5A524',
    red: '#E5484D',
};
/**
 * Light-ground status companions (2026-08-19, dispatcher parity unit).
 * The base status colours are tuned as FILLS and markers; green and amber
 * fail WCAG AA as text on light surfaces, and red needs a pressed/hover
 * step. These deep variants are for status TEXT and hover fills on light
 * ground only (AA on white: green 5.41, amber 5.43, red 6.57). Soft status
 * washes are alpha over surface (e.g. Tailwind `breaker-red/10`), never
 * separate hexes.
 */
export const lightStatusDeep = {
    green: '#027A48',
    amber: '#B54708',
    red: '#B42318',
};
/**
 * Completion-beat-only accent — never a resting state, never identity.
 * A soft wash (low-opacity gradient) behind a STOP/confirmation panel's
 * "done" state. Dark mode uses the same hues as a translucent tint rather
 * than a pastel solid, since a pastel background reads as a light-mode
 * surface no matter what ground sits behind it.
 */
export const celebration = {
    light: { from: '#FFD9A8', to: '#FFB199' },
    dark: { tint: 'rgba(255,140,105,0.16)' },
};
export const radius = {
    /** Cards / panels. */
    card: '12px',
    /** Single-character inputs (the OTP entry) — a fixed choice, independent of the CTA toggle. */
    input: '10px',
    /** CTAs — pill, chosen 2026-08-16 over the 10px-rounded alternative. */
    cta: '9999px',
};
export const motion = {
    duration: '200ms',
    easing: 'cubic-bezier(0, 0, 0.2, 1)', // ease-out
    /** Pressed-state scale for interactive elements. */
    pressScale: 0.98,
    /** Mount-reveal translate distance, in px (paired with a fade). */
    revealPx: 8,
};
export const wordmark = {
    /** "BREAKER" in ink, full stop in the identity blue — chosen 2026-08-16. */
    variant: 'full-stop',
};
export const fonts = {
    sans: 'Inter',
    mono: 'JetBrains Mono',
};
//# sourceMappingURL=index.js.map